import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import { OverviewPanel } from "@/pages/dist/DashboardPro";
import AdminUserPasswordCard from "@/components/admin/AdminUserPasswordCard";
import WalletAdjustCard from "@/components/admin/WalletAdjustCard";
import { ArrowLeft, LogIn, GitMerge } from "lucide-react";
import { toast } from "sonner";
import PlacementMoveModal from "@/components/admin/PlacementMoveModal";

export default function AdminMemberDetail() {
  const { user_code } = useParams();
  const { startImpersonation } = useAuth();
  const [data, setData] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);

  const reload = () => {
    api.get(`/admin/users/${user_code}/overview`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load member"));
  };

  useEffect(() => {
    // Refetches whenever the URL param changes; api and setters are stable.
    setData(null); setErr(null); setRewards(null);
    api.get(`/admin/users/${user_code}/overview`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load member"));
    // Also load rewards progress for this member
    api.get(`/admin/users/${user_code}/rewards`).then((r) => setRewards(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_code]);

  const impersonate = async () => {
    if (!window.confirm(`Login as ${data?.profile?.name || user_code}? This is audit-logged and the session expires in 60 minutes.`)) return;
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/admin/users/${user_code}/impersonate`);
      startImpersonation({ token: resp.token, user: resp.user });
      toast.success(`Now viewing as ${resp.user.name}`);
      // Hard navigation so all providers (Auth, Realtime, etc.) re-initialize cleanly
      // with the new token. A SPA-only nav can race with the localStorage swap.
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout
      title={data ? `${data.profile.name}` : `Loading…`}
      subtitle={data ? `${data.profile.user_code} · ${data.profile.role} · ${data.profile.email}` : user_code}
      actions={
        <div className="flex items-center gap-2">
          {data && data.profile.role !== "admin" && (
            <button
              onClick={() => setMoving(true)}
              data-testid="move-placement-btn"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 border border-royal/30 text-royal hover:bg-royal/5 rounded-sm"
              title="Manually move this member in the binary tree (audit-logged)"
            >
              <GitMerge size={12} /> Move Placement
            </button>
          )}
          {data && data.profile.role !== "admin" && (
            <button
              onClick={impersonate}
              disabled={busy}
              data-testid="login-as-user-btn"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 bg-navy text-gold border border-gold/30 hover:bg-navy/90 rounded-sm disabled:opacity-50"
              title="Login as this user (audit-logged, 60 min session)"
            >
              <LogIn size={12} /> Login as User
            </button>
          )}
          <Link
            to="/admin/members"
            data-testid="back-to-members"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 border border-navy/15 rounded-sm hover:bg-cream/40 text-navy/70"
          >
            <ArrowLeft size={12} /> All Members
          </Link>
        </div>
      }
    >
      {err && <div className="bg-rose/5 border border-rose/20 text-rose px-4 py-3 rounded-sm text-sm" data-testid="member-error">{err}</div>}
      {data ? (
        <>
          <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AdminUserPasswordCard userCode={data.profile.user_code} />
            <WalletAdjustCard
              userCode={data.profile.user_code}
              currentWallets={data.profile.wallets || data.wallets}
              onDone={(w) => setData((prev) => prev ? { ...prev, profile: { ...prev.profile, wallets: w } } : prev)}
            />
          </div>
          <OverviewPanel data={data} rewards={rewards} />
        </>
      ) : !err ? (
        <div className="text-navy/50 text-sm">Loading member overview…</div>
      ) : null}
      {moving && data && (
        <PlacementMoveModal
          member={data.profile}
          onClose={() => setMoving(false)}
          onSaved={reload}
        />
      )}
    </DashboardLayout>
  );
}
